import { useOrdersConfig } from '../config';
import {
  HEADER_ROW_STYLES,
  HEADER_CELL_STYLES,
  DATA_ROW_STYLES,
  DATA_CELL_STYLES,
  COLUMN_SIZE_STYLES,
  WRAPPED_COLUMN_SIZE_STYLES,
  ACTIONS_COLUMN_STYLES,
  humanizeEnumValue,
  TableColumn,
} from '../consts';
import useFilters, { areFiltersModified } from '../hooks/useFilters';
import useSort from '../hooks/useSort';
import { formatAmount } from '../utils';
import CancelOrderCell from './CancelOrderCell';
import CopyButton from './CopyButton';
import OrderCard from './OrderCard';
import OrderDetails from './OrderDetails';
import OrdersFooter from './OrdersFooter';
import Skeleton from './Skeleton';
import SortControls from './SortControls';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import { getDgcOrders } from 'api/requests/dgc/dgcOrders';
import { DgcOrder } from 'api/types/dgc/dgcOrder';
import { ConnectionErrorIllustration } from 'assets/illustrations';
import { SortArrowIcon } from 'assets/svg';
import { useState } from 'react';
import { Role } from 'shared/consts/role';
import useMobileLayout from 'shared/hooks/useMobileLayout';
import { queryKeyBuilder } from 'shared/query';
import { hasAnyOfRole } from 'shared/utils/auth';
import { isDefined } from 'shared/utils/bids';

const MISSING_VALUE = '—';

// Each column declares how it renders (consts/index.ts), so adding one never means editing this function.
function formatCell(order: DgcOrder, column: TableColumn) {
  const value = order[column.key];

  if (value == null) {
    return MISSING_VALUE;
  }
  switch (column.format) {
    case 'date':
      return new Date(value).toISOString().slice(0, 10);
    case 'amount':
      return formatAmount(value as string, order.currency);
    case 'enum':
      return humanizeEnumValue(value as string);
    default:
      return value;
  }
}

function toAriaSort(column: TableColumn, isSortedBy: boolean, sortDir: string) {
  if (!column.sortable) {
    return undefined;
  }
  if (!isSortedBy) {
    return 'none';
  }
  return sortDir === 'ASC' ? 'ascending' : 'descending';
}

function Orders() {
  const urlQueryParams = useFilters();
  const { sortBy, sortDir, toggleSort } = useSort();
  const { columns, features, title } = useOrdersConfig();
  const isMobileLayout = useMobileLayout();
  const [displayedOrderUuid, setDisplayedOrderUuid] = useState<string | undefined>(undefined);

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });
  const canCancel = features.cancel && hasAnyOfRole(userData, Role.GC_Edit);

  const {
    data: ordersData,
    isPending,
    isError,
  } = useQuery({
    queryKey: queryKeyBuilder.dgcOrdersToShow(urlQueryParams),
    queryFn: () => getDgcOrders(urlQueryParams),
  });

  if (isError) {
    return <div>Error</div>;
  }
  if (isPending) {
    return <Skeleton />;
  }
  if (!Array.isArray(ordersData?.orders) || ordersData?.orders.length === 0) {
    return (
      <div className="flex flex-col w-full bg-white rounded-lg divide-y divide-palette-color-bg-grey">
        <div className="w-full grow flex flex-col items-center justify-center p-12">
          <ConnectionErrorIllustration />
          <div className="mt-5 max-w-[360px] text-center font-klarheit text-2xl font-semibold">
            {areFiltersModified(urlQueryParams) ? 'No orders match your filters' : 'There are no orders yet'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isMobileLayout ? (
        <div className="flex flex-col w-full bg-white rounded-lg divide-y divide-palette-color-bg-grey">
          <SortControls columns={columns} sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          {ordersData?.orders.map((order) => (
            <OrderCard
              key={order.uuid}
              order={order}
              columns={columns}
              canCancel={canCancel}
              renderCell={formatCell}
              onSelect={() => setDisplayedOrderUuid(order.uuid)}
            />
          ))}
          <OrdersFooter />
        </div>
      ) : (
        <div className="w-full bg-white rounded-lg overflow-hidden">
          <div className="flex w-full overflow-x-auto scrollbar">
            <div
              role="table"
              aria-label={title}
              className="flex flex-col divide-y divide-palette-color-bg-grey w-max min-w-full shrink-0"
            >
              <div className={HEADER_ROW_STYLES} role="row">
                {columns.map((cellSchema) => {
                  const isSortedBy = !!cellSchema.sortable && sortBy === cellSchema.key;

                  return (
                    <div
                      key={cellSchema.key}
                      role="columnheader"
                      aria-sort={toAriaSort(cellSchema, isSortedBy, sortDir)}
                      className={`${HEADER_CELL_STYLES} ${
                        cellSchema.wrap ? WRAPPED_COLUMN_SIZE_STYLES : COLUMN_SIZE_STYLES
                      }`}
                    >
                      {cellSchema.sortable ? (
                        <button
                          type="button"
                          className="group flex items-center gap-1 hover:text-palette-secondary-dark"
                          onClick={() => toggleSort(cellSchema.key)}
                        >
                          {cellSchema.headerLabel}
                          {/* Only the sorted column carries an indicator; the rest reveal one on hover so the
                              header row isn't littered with arrows pointing nowhere. */}
                          <SortArrowIcon
                            className={`w-2.5 h-2.5 shrink-0 transition ${
                              isSortedBy ? 'opacity-100' : 'opacity-0 group-hover:opacity-40 group-focus:opacity-40'
                            } ${isSortedBy && sortDir === 'ASC' ? 'rotate-180' : ''}`}
                          />
                        </button>
                      ) : (
                        cellSchema.headerLabel
                      )}
                    </div>
                  );
                })}
                {canCancel && <div role="columnheader" className={`${HEADER_CELL_STYLES} ${ACTIONS_COLUMN_STYLES}`} />}
              </div>
              {ordersData?.orders.map((order) => (
                <div
                  key={order.uuid}
                  role="row"
                  className={DATA_ROW_STYLES}
                  onClick={() => setDisplayedOrderUuid(order.uuid)}
                >
                  {columns.map((cellSchema, index) => {
                    const rawValue = order[cellSchema.key];
                    const isCopyable = !!cellSchema.copyable && isDefined(rawValue);

                    return (
                      <div
                        key={`${order.uuid}_${cellSchema.key}`}
                        role="cell"
                        className={`${DATA_CELL_STYLES} ${
                          cellSchema.wrap ? WRAPPED_COLUMN_SIZE_STYLES : COLUMN_SIZE_STYLES
                        } group flex items-center gap-1`}
                      >
                        {/* The first cell carries the keyboard-reachable control, so the row is operable without a mouse. */}
                        {index === 0 ? (
                          <button
                            type="button"
                            aria-label={`View details for order ${order.order_ref ?? ''}`}
                            className={`max-w-full rounded text-left ${
                              cellSchema.wrap ? 'break-all' : 'truncate'
                            }  hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-palette-secondary`}
                          >
                            {formatCell(order, cellSchema)}
                          </button>
                        ) : (
                          <span className={cellSchema.wrap ? 'break-all' : 'truncate'}>
                            {formatCell(order, cellSchema)}
                          </span>
                        )}
                        {isCopyable && <CopyButton value={String(rawValue)} label={cellSchema.headerLabel} />}
                      </div>
                    );
                  })}
                  {canCancel && (
                    <div role="cell" className={`${DATA_CELL_STYLES} ${ACTIONS_COLUMN_STYLES}`}>
                      <CancelOrderCell orderUuid={order.uuid} orderRef={order.order_ref} status={order.status} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <OrdersFooter />
        </div>
      )}
      <OrderDetails orderUuid={displayedOrderUuid} onModalClose={() => setDisplayedOrderUuid(undefined)} />
    </>
  );
}

export default Orders;
